import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faGear, faListCheck } from "@fortawesome/free-solid-svg-icons";
import { aplicarModelo, buscarModelo, listarModelos } from "../api/taskTemplatesApi";
import { getTaskPriorityLabel } from "../utils/taskPriority";
import "./AplicarModeloModal.css";

const hojeYMD = () => new Date().toISOString().slice(0, 10);

/** Mesma conta do servidor, só para a prévia: D+N a partir do início. */
function prever(inicio, diasOffset) {
  if (diasOffset === null || diasOffset === undefined) return null;

  const data = new Date(`${inicio}T00:00:00`);
  if (Number.isNaN(data.getTime())) return null;

  data.setDate(data.getDate() + Number(diasOffset));
  return data.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

const AplicarModeloModal = ({ show, onHide, onAplicado, workspaceId, projetos = [] }) => {
  const navigate = useNavigate();

  const [modelos, setModelos] = useState([]);
  const [modeloId, setModeloId] = useState("");
  const [modelo, setModelo] = useState(null);

  const [descricao, setDescricao] = useState("");
  const [dataInicio, setDataInicio] = useState(hojeYMD());
  const [idProjeto, setIdProjeto] = useState("");

  const [carregando, setCarregando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro("");

    try {
      setModelos(await listarModelos(workspaceId));
    } catch (error) {
      setErro(error?.message || "Nao foi possivel carregar os modelos.");
    } finally {
      setCarregando(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (!show) return;

    setModeloId("");
    setModelo(null);
    setDescricao("");
    setDataInicio(hojeYMD());
    setIdProjeto("");
    setErro("");
    carregar();
  }, [show, carregar]);

  useEffect(() => {
    if (!modeloId) {
      setModelo(null);
      return;
    }

    buscarModelo(modeloId)
      .then((registro) => {
        setModelo(registro);
        // O nome do modelo vira sugestao; voce troca por "Implantacao — Acme".
        setDescricao((atual) => atual || registro?.nome || "");
      })
      .catch(() => setErro("Nao foi possivel carregar os passos do modelo."));
  }, [modeloId]);

  const itens = useMemo(() => modelo?.itens || [], [modelo]);

  async function confirmar() {
    if (!modeloId || !descricao.trim()) return;

    setAplicando(true);
    setErro("");

    try {
      const resultado = await aplicarModelo(modeloId, {
        descricao: descricao.trim(),
        data_inicio: dataInicio,
        id_projeto: idProjeto || null,
        id_workspace: workspaceId,
      });

      onAplicado?.(resultado);
      onHide();
    } catch (error) {
      console.error("AplicarModelo: erro ao aplicar", error);
      setErro(
        error?.response?.data?.message || error?.message || "Nao foi possivel aplicar o modelo."
      );
    } finally {
      setAplicando(false);
    }
  }

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faListCheck} /> Criar a partir de um modelo
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {carregando ? (
          <div className="am-carregando"><Spinner size="sm" /></div>
        ) : modelos.length === 0 ? (
          <div className="am-vazio">
            <strong>Nenhum modelo ainda.</strong>
            <span>
              Modelo é a checklist que você repete — implantação, onboarding, fechamento.
              Monte uma vez e reutilize a cada cliente.
            </span>
            <Button
              variant="primary"
              onClick={() => {
                onHide();
                navigate("/modelos");
              }}
            >
              Criar meu primeiro modelo
            </Button>
          </div>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Modelo</Form.Label>
              <Form.Select value={modeloId} onChange={(e) => setModeloId(e.target.value)}>
                <option value="">Escolha um modelo...</option>
                {modelos.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.nome} ({item.total_itens} passos)
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <div className="am-linha">
              <Form.Group className="flex-grow-1">
                <Form.Label>Nome da tarefa</Form.Label>
                <Form.Control
                  type="text"
                  value={descricao}
                  placeholder="Ex: Implantacao — Cliente Acme"
                  onChange={(e) => setDescricao(e.target.value)}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Início</Form.Label>
                <Form.Control
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                />
              </Form.Group>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Projeto (opcional)</Form.Label>
              <Form.Select value={idProjeto} onChange={(e) => setIdProjeto(e.target.value)}>
                <option value="">Sem projeto</option>
                {projetos.map((projeto) => (
                  <option key={projeto.value} value={projeto.value}>{projeto.label}</option>
                ))}
              </Form.Select>
            </Form.Group>

            {itens.length > 0 && (
              <div className="am-previa">
                <div className="am-previa-head">
                  <strong>{itens.length} passos serão criados</strong>
                  <span>com prazos contados a partir de {prever(dataInicio, 0)}</span>
                </div>

                <ol className="am-passos">
                  {itens.map((item) => (
                    <li key={item.id}>
                      <span className="am-passo-nome">{item.descricao}</span>
                      <span className="am-passo-meta">
                        {item.responsavel && <em>{item.responsavel}</em>}
                        <span className="am-passo-prio">
                          {getTaskPriorityLabel(item.prioridade)}
                        </span>
                        {item.dias_offset !== null && item.dias_offset !== undefined ? (
                          <strong>{prever(dataInicio, item.dias_offset)}</strong>
                        ) : (
                          <strong className="sem-prazo">sem prazo</strong>
                        )}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {erro && <Alert variant="danger" className="mt-3 mb-0 py-2">{erro}</Alert>}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="justify-content-between">
        <Button
          variant="link"
          className="am-gerenciar"
          onClick={() => {
            onHide();
            navigate("/modelos");
          }}
        >
          <FontAwesomeIcon icon={faGear} /> Gerenciar modelos
        </Button>

        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onHide} disabled={aplicando}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={confirmar}
            disabled={aplicando || !modeloId || !descricao.trim()}
          >
            {aplicando ? "Criando..." : <>Criar <FontAwesomeIcon icon={faArrowRight} /></>}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AplicarModeloModal;
